import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Alert, Box, Button, IconButton, List, ListItem, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { taskErrorMessage } from '../../services/TaskTrackingService';

/**
 * Admin editor for a list tasks pick from: add, rename, hide and reorder. Hiding keeps the label
 * on what already has it; it just stops being offered. noun names a new entry ("category").
 */
const TaskListEditor = ({ noun, items, service, refresh }) => {
    const [names, setNames] = useState({});
    const [newLabel, setNewLabel] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setNames(Object.fromEntries(items.map((c) => [c.key, c.label])));
    }, [items]);

    const save = async (fn) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
            await refresh();
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    // Swaps order with the neighbour; orders are rewritten 1..n so gaps and ties don't matter
    const move = (index, delta) =>
        save(async () => {
            const list = [...items];
            const [item] = list.splice(index, 1);
            list.splice(index + delta, 0, item);
            await Promise.all(list.map((c, i) => (c.order === i + 1 ? null : service.update(c.key, { order: i + 1 }))));
        });

    const add = () =>
        save(async () => {
            await service.create(newLabel.trim());
            setNewLabel('');
        });

    return (
        <>
            <List dense>
                {items.map((c, i) => {
                    const renamed = (names[c.key] ?? c.label).trim();
                    return (
                        <ListItem key={c.key} disableGutters sx={{ gap: 1 }}>
                            <TextField
                                size="small"
                                value={names[c.key] ?? c.label}
                                onChange={(e) => setNames((n) => ({ ...n, [c.key]: e.target.value }))}
                                inputProps={{ 'aria-label': `Name of ${c.label}` }}
                                sx={{ flex: 1, opacity: c.hidden ? 0.6 : 1 }}
                            />
                            {renamed && renamed !== c.label && (
                                <Button size="small" disabled={busy} onClick={() => save(() => service.update(c.key, { label: renamed }))}>
                                    Rename
                                </Button>
                            )}
                            <Button size="small" disabled={busy} onClick={() => save(() => service.update(c.key, { hidden: !c.hidden }))}>
                                {c.hidden ? 'Show' : 'Hide'}
                            </Button>
                            <IconButton size="small" aria-label={`Move ${c.label} up`} disabled={busy || i === 0} onClick={() => move(i, -1)}>
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                aria-label={`Move ${c.label} down`}
                                disabled={busy || i === items.length - 1}
                                onClick={() => move(i, 1)}
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                        </ListItem>
                    );
                })}
            </List>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    label={`New ${noun}`}
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && newLabel.trim()) add();
                    }}
                />
                <Button variant="outlined" disabled={busy || !newLabel.trim()} onClick={add}>
                    Add
                </Button>
            </Box>
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}
        </>
    );
};

export default TaskListEditor;
